import React, { useState } from 'react';
import { FileCode2, Pencil } from 'lucide-react';
import { Badge, EmptyState, ErrorState, LoadingState, SeverityBadge } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { adminApi, formatDate, plural, useAdminQuery } from './adminApi';
import { AdminSection, FilterChips, Pager, RefreshError, SearchField, Toolbar } from './AdminUi';
import RuleEditorDialog from './RuleEditorDialog';

const PAGE = 50;
const RULE_FILTERS = [
  { value: 'any', label: 'All briefs' },
  { value: 'with_rules', label: 'With rules' },
  { value: 'without_rules', label: 'Without rules' },
];

/**
 * Detection rules per brief. Rules are generated only from vetted indicators,
 * so many briefs legitimately have none; an admin can curate the indicator
 * list and preview/save the rules from it.
 */
export default function RulesTab() {
  const [q, setQ] = useState('');
  const [ruleStatus, setRuleStatus] = useState('any');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState(null);

  const { data, error, loading, reload } = useAdminQuery(
    () => adminApi.get('/admin/rules/overview', { params: { q: q.trim() || undefined, rule_status: ruleStatus, limit: PAGE, offset } }).then((r) => r.data),
    [q, ruleStatus, offset]
  );

  const rows = data?.attacks || [];

  return (
    <AdminSection
      title="Detection rules"
      count={data ? data.total : null}
      description="YARA and Sigma rules are built only from validated, attacker-controlled indicators. A brief with no usable indicator has no rule, by design."
    >
      <Toolbar>
        <SearchField value={q} onChange={(v) => { setQ(v); setOffset(0); }} label="Search briefs" placeholder="Search brief title or actor" />
        <FilterChips label="Rule status" options={RULE_FILTERS} value={ruleStatus} onChange={(v) => { setRuleStatus(v); setOffset(0); }} testIdPrefix="rules-filter" />
      </Toolbar>

      {loading ? (
        <LoadingState label="briefs" rows={6} />
      ) : error && !data ? (
        <ErrorState title="Couldn't load briefs" error={error} onRetry={reload} />
      ) : !rows.length ? (
        <EmptyState icon={FileCode2} title="No briefs match" description={q || ruleStatus !== 'any' ? 'Try clearing the search or filter.' : 'Briefs appear here after the first articles are analyzed.'} />
      ) : (
        <>
          <RefreshError error={error} onRetry={reload} what="briefs" />
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--stack" data-testid="rules-table">
              <thead>
                <tr>
                  <th scope="col">Brief</th>
                  <th scope="col">Severity</th>
                  <th scope="col" className="adm-num">Indicators</th>
                  <th scope="col" className="adm-num">YARA</th>
                  <th scope="col" className="adm-num">Sigma</th>
                  <th scope="col">Discovered</th>
                  <th scope="col"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} data-testid="rules-row">
                    <td data-label="Brief" className="adm-break">
                      <div className="adm-strong">{a.name}</div>
                      <div className="adm-meta">
                        {a.threat_actor ? `${a.threat_actor} · ` : ''}{a.content_type}
                        {a.campaign && <> · <Badge size="sm" plain>Hunt: {a.campaign.status}</Badge></>}
                      </div>
                    </td>
                    <td data-label="Severity"><SeverityBadge severity={a.severity} size="sm" short /></td>
                    <td data-label="Indicators" className="adm-num">{a.ioc_count}</td>
                    <td data-label="YARA" className="adm-num">{a.yara_count || <span className="adm-muted">none</span>}</td>
                    <td data-label="Sigma" className="adm-num">{a.sigma_count || <span className="adm-muted">none</span>}</td>
                    <td data-label="Discovered" className="adm-mono adm-nowrap adm-secondary">{formatDate(a.discovered_at)}</td>
                    <td className="adm-actions-cell">
                      <Button variant="outline" size="sm" onClick={() => setEditing(a.id)} data-testid="rules-edit-btn">
                        <Pencil aria-hidden="true" /> Edit rules<span className="sr-only"> for {a.name}</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={PAGE} total={data.total} onChange={setOffset} label="Briefs pagination" />
        </>
      )}

      <RuleEditorDialog
        attackId={editing}
        onOpenChange={(open) => { if (!open) setEditing(null); }}
        onSaved={() => reload()}
      />
      <span className="sr-only" aria-live="polite">{data ? plural(data.total, 'brief') : ''}</span>
    </AdminSection>
  );
}
